import * as React from 'react';
import { PopoverCommonProps } from '../../common';

export interface DragEndEvent {
  active: {
    id: string;
  };
  over: {
    id: string;
  } | null;
}

export interface DragStartEvent {
  active: {
    id: string;
  };
}

export interface DraggableTableRowProps<E> {
  id: string;
  index: number;
  children?: E;
  isDragOverlay?: boolean;
  isDragAndDropDisabled?: boolean;
}

export interface TableDragAndDrop<
  E extends React.ReactElement<React.HTMLAttributes<HTMLElement>>,
> {
  DroppableTableContext: React.ComponentType<{
    children?: React.ReactNode;
    items: { id: string }[];
    className?: string;
    style?: React.CSSProperties;
    onDragEnd?(event: DragEndEvent): unknown;
    onDragStart?(event: DragStartEvent): unknown;
    onDragCancel?(event: DragEndEvent): unknown;
    isDragAndDropDisabled?: boolean;
    renderRow: (args: {
      rowNum: number;
      rowData: any;
      isDragOverlay: boolean;
    }) => React.ReactElement;
  }>;
  DraggableTableRow: React.ComponentType<DraggableTableRowProps<E>>;
  createDragHandleColumn?: (tableProps: DataTableProps) => DataTableColumn;
}

export interface DataTableResizeProps {
  onColumnResize?: (columnId: string, newWidth: number) => void;
  onColumnResizeStart?: (
    columnId: string,
    startWidth: number,
    event: React.MouseEvent,
  ) => void;
  onColumnResizeEnd?: (
    columnId: string,
    endWidth: number,
    event: React.MouseEvent,
  ) => void;
  minColumnWidth?: number;
  maxColumnWidth?: number;
}

export type RowDataDefaultType = any;
export interface DataTableProps<RowData = RowDataDefaultType> {
  dataHook?: string;
  id?: string;
  data?: RowData[];
  columns: DataTableColumn<RowData>[];
  /** @default false */
  showHeaderWhenEmpty?: boolean;
  rowDataHook?: string | DataTableRowDataHookFn<RowData>;
  rowClass?: string;
  headerRowClass?: string;
  headerClass?: string;
  dynamicRowClass?: (rowData: RowData, rowNum: number) => string;
  /** @default null */
  isRowSelected?: (rowData: RowData, rowNum: number) => boolean;
  isRowHighlight?: (rowData: RowData, rowNum: number) => boolean;
  isRowActive?: (rowData: RowData, rowNum: number) => boolean;
  onRowClick?: (rowData: RowData, rowNum: number) => void;
  onMouseEnterRow?: (rowData: RowData, rowNum: number) => void;
  onMouseLeaveRow?: (rowData: RowData, rowNum: number) => void;
  onSortClick?: (column: DataTableColumn, colNum: number) => void;
  /** @default false */
  infiniteScroll?: boolean;
  /** @default 20 */
  itemsPerPage?: number;
  /** @default '100%' */
  width?: string;
  /** @default 'standard' */
  skin?: DataTableSkin;
  /** @default null */
  loadMore?: () => void;
  /** @default false */
  hasMore?: boolean;
  /** @default true */
  initialLoad?: boolean;
  loader?: React.ReactNode;
  /** @default true */
  useWindow?: boolean;
  /** @default null */
  scrollElement?: HTMLElement | React.RefObject<any>;
  viewportRef?: React.RefObject<HTMLElement | null>;
  /** @default 'small' */
  rowVerticalPadding?: DataTableRowVerticalPadding;
  rowDetails?: (rowData: RowData, rowNum: number) => React.ReactNode;
  /** @default false */
  removeRowDetailsPadding?: boolean;
  allowMultiDetailsExpansion?: boolean;
  hideHeader?: boolean;
  hideHeaderAccessible?: boolean;
  /** @default true */
  showLastRowDivider?: boolean;
  isApplyColumnWidthStyle?: boolean;
  virtualized?: boolean;
  virtualizedTableHeight?: number;
  /** @default 60 */
  virtualizedLineHeight?: number;
  virtualizedListRef?: React.LegacyRef<any>;
  selectedRowsIds?: (string | number)[];
  /** @default false */
  horizontalScroll?: boolean;
  /** @default 0 */
  stickyColumns?: number;
  isRowDisabled?: (rowData: RowData) => boolean;
  infiniteScrollRef?: React.Ref<any>;
  /** @default null */
  dragAndDrop?: any;
  resizable?: boolean;
  resizeProps?: DataTableResizeProps;
}

export default class DataTable<
  RowData = RowDataDefaultType,
> extends React.Component<DataTableProps<RowData>> {}

export type DataTableColumnAlign = 'start' | 'center' | 'end';
export type DataTableRowDataHookFn<RowData = RowDataDefaultType> = (
  rowData: RowData,
  rowNum: number,
) => string;
export type DataTableSkin = 'standard' | 'neutral';
export type DataTableRowVerticalPadding =
  | 'xxTiny'
  | 'xTiny'
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large';
export interface PopoverPropsWithContent extends PopoverCommonProps {
  content?: React.ReactNode;
}

export interface DataTableColumnResizeProps {
  id: string;
  disabled?: boolean;
  hideDisabledResizeHandle?: boolean;
}

export type DataTableColumn<RowData = RowDataDefaultType> = {
  dataHook?: string;
  key?: React.Key;
  title: React.ReactNode;
  render: (row: RowData, rowNum: number) => React.ReactNode;
  /** @default '100%' */
  width?: string | number;
  important?: boolean;
  sortable?: boolean;
  sortDescending?: boolean;
  style?:
    | React.CSSProperties
    | ((
        column: DataTableColumn,
        rowData: RowData,
        rowNum: number,
      ) => React.CSSProperties);
  infoTooltipProps?: PopoverPropsWithContent;
  align?: DataTableColumnAlign;
  stickyActionCell?: boolean;
  resizeProps?: DataTableColumnResizeProps;
  /** Custom content rendered at the far right of the column header, after the sort arrow and info tooltip. */
  titleSuffix?: React.ReactNode;
};
